/**
 * Token storage and permission helpers
 * Handles JWT parsing, token storage, and permission/module checking
 */

const AUTH_KEYS = {
  TOKEN: "token",
  USER_ID: "userId",
  USER_ROLE: "userRole",
  USER_MODULES: "userModules",
  USER_PERMISSIONS: "userPermissions",
} as const;

/**
 * Parse JWT token and extract payload
 */
export const parseJwt = (token: string): Record<string, any> => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("[TokenHelper] Error parsing JWT:", e);
    return {};
  }
};

/**
 * Store user data in localStorage
 */
export const storeUserData = (userData: {
  id: string;
  role: string;
  modules: string[];
  permissions: any[];
}): void => {
  if (typeof window === "undefined") return;

  try {
    localStorage.setItem(AUTH_KEYS.USER_ID, userData.id);
    localStorage.setItem(AUTH_KEYS.USER_ROLE, userData.role);
    localStorage.setItem(AUTH_KEYS.USER_MODULES, JSON.stringify(userData.modules));
    localStorage.setItem(
      AUTH_KEYS.USER_PERMISSIONS,
      JSON.stringify(userData.permissions)
    );
  } catch (err) {
    console.error("[TokenHelper] Error storing user data:", err);
  }
};

/**
 * Retrieve user data from localStorage
 */
export const getUserData = (): {
  id: string | null;
  role: string | null;
  modules: string[];
  permissions: any[];
} => {
  if (typeof window === "undefined") {
    return { id: null, role: null, modules: [], permissions: [] };
  }

  try {
    return {
      id: localStorage.getItem(AUTH_KEYS.USER_ID),
      role: localStorage.getItem(AUTH_KEYS.USER_ROLE),
      modules: JSON.parse(localStorage.getItem(AUTH_KEYS.USER_MODULES) || "[]"),
      permissions: JSON.parse(
        localStorage.getItem(AUTH_KEYS.USER_PERMISSIONS) || "[]"
      ),
    };
  } catch (err) {
    console.error("[TokenHelper] Error retrieving user data:", err);
    return { id: null, role: null, modules: [], permissions: [] };
  }
};

/**
 * Clear only auth-related keys from localStorage
 * Preserves other application data
 */
export const clearAuthStorage = (): void => {
  if (typeof window === "undefined") return;

  try {
    Object.values(AUTH_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
  } catch (err) {
    console.error("[TokenHelper] Error clearing auth storage:", err);
  }
};

/**
 * Check if user has access to a given module
 */
export const hasModule = (
  modules: string[] | undefined,
  moduleKey: string
): boolean => {
  if (!modules) return false;
  const normalizedInput = moduleKey.toLowerCase().replace(" webportal", "");
  return modules.some(
    (m) => m.toLowerCase().replace(" webportal", "") === normalizedInput
  );
};

/**
 * Check if user has a specific permission (by id or name)
 */
export const hasPermission = (
  permissions: any[] | undefined,
  perm: string | number,
  applicationType?: string
): boolean => {
  if (!permissions || !Array.isArray(permissions)) return false;

  const normalizedPerm =
    typeof perm === "string" ? perm.toLowerCase() : perm;

  return permissions.some((rp) => {
    // Check applicationType if provided
    if (applicationType && rp.applicationType !== applicationType) {
      return false;
    }

    if (typeof perm === "string") {
      return (rp.permissions || []).some(
        (p: string | number) =>
          typeof p === "string"
            ? p.toLowerCase() === normalizedPerm
            : p === normalizedPerm
      );
    }

    // Numeric permission ID support
    return (rp.permissions || []).includes(normalizedPerm);
  });
};

/**
 * Check if user has permission from context or localStorage
 * Useful for SSR hydration or page refresh scenarios
 */
export const hasPermissionWithFallback = (
  contextPermissions: any[] | undefined,
  perm: string | number,
  applicationType?: string
): boolean => {
  // 1️⃣ Check context first (already available after login)
  if (hasPermission(contextPermissions, perm, applicationType)) {
    return true;
  }

  // 2️⃣ Fallback: read from localStorage (SSR hydration or refresh)
  if (typeof window !== "undefined") {
    const storedPerms = getUserData().permissions;
    if (hasPermission(storedPerms, perm, applicationType)) {
      return true;
    }
  }

  return false;
};
